import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Droplets, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSEO({ title: "Admin Login", description: "Wasserlab admin dashboard login.", path: "/login" });

  useEffect(() => { if (user) navigate("/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-navy-900">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative flex flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-amber-500">
              <Droplets className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="leading-none">
              <span className="block font-heading text-xl font-extrabold text-white">Wasserlab</span>
              <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-500">EHS Remote</span>
            </span>
          </div>
          <div>
            <ShieldCheck className="h-10 w-10 text-amber-500" aria-hidden="true" />
            <h2 className="mt-6 font-heading text-3xl font-bold text-white">Compliance Operations Dashboard</h2>
            <p className="mt-3 max-w-md text-slate-400">Manage leads, assessment requests, uploaded documents and pipeline analytics.</p>
          </div>
          <p className="text-xs text-slate-500">Authorized personnel only.</p>
        </div>
      </div>

      <div className="flex w-full lg:w-1/2 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-2xl font-bold text-white">Sign in</h1>
          <p className="mt-1 text-sm text-slate-400">Access the Wasserlab admin dashboard.</p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-white/5 border-white/15 text-white" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-white/5 border-white/15 text-white" data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-6 py-3 text-base font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400 disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
